import * as _ from "lodash";
import { BehaviorSubject } from "rxjs";
import { ISelectOption } from "../../../selectOptions";
import { ISelectFilter } from "../../interfaces/selectFilter.interface";

/**
 * implementation for dynamic multi select filter.
 */
export class DynamicMultiSelectFilters implements ISelectFilter {
    private _options$: BehaviorSubject<ISelectOption[]>;
    private _filterIds: Set<string>;
    private _optionsById: _.Dictionary<ISelectOption>;

    constructor(options: ISelectOption[] = [], filterIdsStartingValue: string[] = []) {
        this._options$ = new BehaviorSubject(options);
        this._optionsById = _.keyBy(options, "id");
        this._filterIds = new Set(filterIdsStartingValue);
    }

    public updateIfNeeded(selectedOptions: ISelectOption[]): boolean {
        const newSelectedOptionsIds = Object.keys(_.keyBy(selectedOptions, "id"));

        if (!this._shouldUpdateSelectedOptions(newSelectedOptionsIds)) {
            this._filterIds = new Set(newSelectedOptionsIds);

            this._options$.next(_.union(this._options$.value, selectedOptions));
            this._options$.value.forEach((option) => {
                this._optionsById[option.id] = option;
            });
            return true;
        }

        return false;
    }

    public getSelectedFiltersIds(): string[] {
        return Array.from(this._filterIds);
    }

    public getSelectedFiltersValues(): string[] {
        const values: string[] = [];
        if (this._options$.value.length == 0) {
            return [];
        }

        this._filterIds.forEach((id) => {
            if (this._optionsById[id]) {
                values.push(this._optionsById[id].value);
            }
        });
        return values;
    }

    public getSelectedOptions(): ISelectOption[] {
        const options: ISelectOption[] = [];
        if (this._options$.value.length == 0) {
            return [];
        }

        this._filterIds.forEach((selectedId) => {
            if (selectedId in this._optionsById) {
                options.push(this._optionsById[selectedId]);
            }
        });

        return options;
    }

    public getOptions$(): BehaviorSubject<ISelectOption[]> {
        return this._options$;
    }

    public updateOptions(options: ISelectOption[]): boolean {
        const fistTimeOptions: boolean = this._options$.value.length === 0;
        this._options$.next(_.union(this._options$.getValue(), options));
        this._optionsById = _.keyBy(options, "id");

        // filter out old selected options if not relevant with new selection options
        const newSelectedOptions: string[] = [];

        this._filterIds.forEach((filterId) => {
            if (filterId in this._optionsById) {
                newSelectedOptions.push(filterId);
            }
        });

        if (fistTimeOptions && newSelectedOptions.length > 0) {
            return true;
        }

        // update filter ids if selected options should be updated
        if (!this._shouldUpdateSelectedOptions(newSelectedOptions)) {
            this._filterIds = new Set(newSelectedOptions);
            return true;
        }

        return false;
    }

    public updateByValuesIfNeeded(values: string[]): boolean {
        const optionsOfReceivedValues: ISelectOption[] = [];

        if (this._filterIds.size > 0 && values.length == 0) {
            this._filterIds = new Set([]);
            return true;
        }

        if (this._options$.value.length == 0) {
            this._filterIds = new Set<string>(values);
            return false;
        }

        this._options$.value.forEach((option) => {
            const index = values.indexOf(option.value);
            if (index !== -1) {
                optionsOfReceivedValues.push(option);
            }
        });

        if (optionsOfReceivedValues.length > 0) {
            return this.updateIfNeeded(optionsOfReceivedValues);
        }

        return false;
    }

    private _shouldUpdateSelectedOptions(newSelectedOptionsIds: string[]): boolean {
        return _.isEqual(Array.from(this._filterIds).sort(), newSelectedOptionsIds.sort());
    }
}
